import type {
  VisibleArmyCardViewV1,
  VisibleBattlefieldCommanderV1,
  VisibleBattlefieldLandV1,
  VisibleCardViewV1,
} from "@fate-kingdom/engine";

import { AlphaGameIcon } from "../icons/AlphaGameIcon";
import { getAlphaCardPresentation } from "../presentation/alpha-card-presentation";

type AlphaFieldCardState = "active" | "committed" | "defeated";

interface FieldCardFrameProps {
  readonly card: VisibleCardViewV1;
  readonly children: React.ReactNode;
  readonly className: string;
  readonly state: AlphaFieldCardState;
  readonly onInspect?: () => void;
}

function FieldCardFrame({ card, children, className, state, onInspect }: FieldCardFrameProps) {
  const presentation = getAlphaCardPresentation(card);
  const classes = [
    "alpha-field-card",
    className,
    `alpha-field-card--${presentation.faction}`,
    state === "committed" ? "is-committed" : "",
    state === "defeated" ? "is-defeated" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const contents = (
    <>
      <span className="alpha-field-card__art" aria-hidden="true">
        {presentation.artUrl === undefined ? (
          <AlphaGameIcon name={presentation.accent} />
        ) : (
          <img src={presentation.artUrl} alt="" />
        )}
      </span>
      {children}
      {state === "committed" ? <span className="alpha-field-card__seal">Committed</span> : null}
    </>
  );

  return onInspect === undefined ? (
    <div
      className={classes}
      data-alpha-field-card-id={card.cardInstanceId}
      aria-label={`${presentation.displayName} card`}
    >
      {contents}
    </div>
  ) : (
    <button
      className={classes}
      type="button"
      data-alpha-field-card-id={card.cardInstanceId}
      aria-label={`Inspect field card ${presentation.displayName}`}
      onClick={onInspect}
    >
      {contents}
    </button>
  );
}

export function AlphaFieldLandCard({
  land,
  state,
  onInspect,
}: {
  readonly land: VisibleBattlefieldLandV1;
  readonly state: AlphaFieldCardState;
  readonly onInspect: () => void;
}) {
  const card = land.cards[0]!;
  const presentation = getAlphaCardPresentation(card);
  const damaged = land.currentControl < land.maximumControl;
  return (
    <FieldCardFrame
      card={card}
      className={`alpha-field-card--land ${damaged ? "is-damaged" : ""}`}
      state={state}
      onInspect={onInspect}
    >
      <span className="alpha-field-card__crown">
        <span>{presentation.displayName}</span>
        <b>Stage {land.stage}</b>
      </span>
      <span className="alpha-field-card__control">
        <AlphaGameIcon name="control" aria-hidden="true" />
        <b>{land.currentControl}</b>
        <span>/ {land.maximumControl}</span>
      </span>
      {damaged ? (
        <span
          className="alpha-field-card__damage"
          aria-label={`${String(land.maximumControl - land.currentControl)} Control lost`}
        />
      ) : null}
    </FieldCardFrame>
  );
}

export function AlphaFieldCommanderCard({
  commander,
  state,
}: {
  readonly commander: VisibleBattlefieldCommanderV1;
  readonly state: AlphaFieldCardState;
}) {
  const presentation = getAlphaCardPresentation(commander);
  return (
    <FieldCardFrame card={commander} className="alpha-field-card--commander" state={state}>
      <span className="alpha-field-card__crown">
        <span>{presentation.displayName}</span>
      </span>
      <span className="alpha-field-card__mini-stats">
        <b>
          <AlphaGameIcon name="attack" aria-hidden="true" /> {commander.attack ?? 0}
        </b>
        <b>
          <AlphaGameIcon name="guard" aria-hidden="true" /> {commander.guard ?? 0}
        </b>
        {commander.armyCapacity === undefined ? null : (
          <b>
            <AlphaGameIcon name="capacity" aria-hidden="true" /> {commander.armyCapacity ?? 0}
          </b>
        )}
      </span>
    </FieldCardFrame>
  );
}

export function AlphaStackedArmyCard({
  army,
  index,
  state,
}: {
  readonly army: VisibleArmyCardViewV1;
  readonly index: number;
  readonly state: AlphaFieldCardState;
}) {
  const presentation = getAlphaCardPresentation(army);
  return (
    <FieldCardFrame card={army} className="alpha-field-card--army" state={state}>
      <span className="alpha-field-card__army-index" aria-hidden="true">
        {index + 1}
      </span>
      <span className="alpha-field-card__army-name">{presentation.displayName}</span>
      <span className="alpha-field-card__mini-stats">
        <b>
          <AlphaGameIcon name="attack" aria-hidden="true" /> {army.attack}
        </b>
        <b>
          <AlphaGameIcon name="guard" aria-hidden="true" /> {army.guard}
        </b>
        <b>
          <AlphaGameIcon name="size" aria-hidden="true" /> {army.armySize}
        </b>
      </span>
    </FieldCardFrame>
  );
}
